// One of MedSnapp's cast (brand/cast.ts) in the mood of a moment. Works in any look; MascotSlot uses it only under the
// medsnapp look, and the front page may use it anywhere.
//
//   right    a spring pop up from below, a little bounce, a short sparkle burst
//   wrong    a quick shake, then a slight tilt and droop
//   partial  a wobble                      none   a slow sleepy sway
//   podium   a jump, with a small crown above (drawn here)      idle   a gentle bob
//
// The drawing is a plain <img> from /cast/<name>.svg, so the bundle never carries it (the glyph file below 40 px).
// Motion is CSS only (cast.css), transform and opacity; reduced motion shows each mood's end state. The box is
// size × size and never changes, whatever the mood; the crown and sparks paint outside it. If the file fails to load,
// the Pulse stands in, in the same mood.
//
// The face (phase 2, 3 Nov 2026): once the drawing's text has been fetched (same file, cached), it is put inline and
// MedSnapp's own face rig (brand/rig.ts) sets the mood's pose on it, so the face changes too: right joy, wrong sad,
// partial surprised, none sleepy, podium proud, idle neutral. Transform and opacity transitions only; the mouth is
// swapped. Until then, or if the text cannot be fetched or is not the rig's shape, the plain drawing stays.
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { MOOD_POSE, poseCast, uniqueIds } from './rig';
import { prefersReducedMotion } from '@/motion/reduced';
import { Pulse, type PulseMood } from './Pulse';
import { castSrc, isCastName, DEFAULT_CAST } from './cast';
import './cast.css';

export type CastMood = 'right' | 'wrong' | 'partial' | 'none' | 'podium' | 'idle';

/** The Pulse's mood for each cast mood, when the drawing cannot be shown. */
export const CAST_FALLBACK_MOOD: Readonly<Record<CastMood, PulseMood>> = {
  right: 'spike', wrong: 'jolt', partial: 'steady', none: 'flat', podium: 'crown', idle: 'steady',
};

/** Below this size the simplified glyph is drawn. */
export const GLYPH_BELOW = 40;

export interface CastMascotProps {
  name: string;
  mood: CastMood;
  size: number;
  className?: string;
  /** What a screen reader hears; without it the mascot is decorative. */
  label?: string;
}

const texts = new Map<string, Promise<string | undefined>>();
/** The drawing's text, fetched once per page; undefined where it cannot be fetched (tests, offline, not an SVG). */
export function castText(name: string): Promise<string | undefined> {
  let t = texts.get(name);
  if (!t) {
    t = (async () => {
      try {
        if (typeof fetch !== 'function' || typeof location === 'undefined' || !/^https?:/.test(location.protocol)) return undefined;
        const res = await fetch(castSrc(name));
        const text = res.ok ? await res.text() : '';
        return /^\s*<svg[\s>]/.test(text) && !/<script|\son\w+=/i.test(text) ? text : undefined;
      } catch { return undefined; }
    })();
    texts.set(name, t);
  }
  return t;
}

/** The drawing inline, posed by the face rig. */
function RiggedDrawing({ text, mood, size, onPosed }: { text: string; mood: CastMood; size: number; onPosed: (ok: boolean) => void }) {
  const ref = useRef<HTMLSpanElement>(null);
  const uid = `c${useId().replace(/[^A-Za-z0-9]/g, '')}`;
  const [html] = useState(() => uniqueIds(text, uid).replace(/\swidth="\d+"\s+height="\d+"/, ` width="${size}" height="${size}"`));
  useLayoutEffect(() => {
    const svg = ref.current?.querySelector('svg');
    if (!svg) { onPosed(false); return; }
    svg.setAttribute('width', String(size)); svg.setAttribute('height', String(size));
    if (!poseCast(svg, 'neutral')) { onPosed(false); return; }
    onPosed(true);
    if (prefersReducedMotion()) { poseCast(svg, MOOD_POSE[mood]); return; }
    svg.getBoundingClientRect(); // the neutral face is drawn first, so the move to the mood's face transitions
    const raf = requestAnimationFrame(() => poseCast(svg, MOOD_POSE[mood]));
    return () => cancelAnimationFrame(raf);
  }, [mood, size]); // eslint-disable-line react-hooks/exhaustive-deps
  return <span ref={ref} className="rd-cast-rig" style={{ width: size, height: size, display: 'block' }} dangerouslySetInnerHTML={{ __html: html }} />;
}

export function CastMascot({ name, mood, size, className = '', label }: CastMascotProps) {
  const cast = isCastName(name) ? name : DEFAULT_CAST;
  const [failed, setFailed] = useState(false);
  const [text, setText] = useState<{ cast: string; text: string }>();
  const [rigged, setRigged] = useState<boolean>();
  useEffect(() => { setFailed(false); setRigged(undefined); }, [cast]);
  const glyph = size < GLYPH_BELOW;
  useEffect(() => {
    if (glyph) return;
    let live = true;
    void castText(cast).then((t) => { if (live && t) setText({ cast, text: t }); });
    return () => { live = false; };
  }, [cast, glyph]);
  const inline = !glyph && text?.cast === cast && rigged !== false;
  const a11y = label ? { role: 'img' as const, 'aria-label': label } : { 'aria-hidden': true as const };

  if (failed) {
    return (
      <span data-part="cast-mascot" data-cast={cast} data-mood={mood} data-fallback="pulse" {...a11y}
        className={`relative inline-flex shrink-0 items-center justify-center ${className}`}
        style={{ width: size, height: size, overflow: 'visible' }}>
        <Pulse mood={CAST_FALLBACK_MOOD[mood]} width={size} height={Math.round(size * 0.42)} glow={mood === 'right' || mood === 'podium'} />
      </span>
    );
  }

  return (
    <span data-part="cast-mascot" data-cast={cast} data-mood={mood} data-rig={inline && rigged ? '' : undefined} {...a11y}
      className={`rd-cast relative inline-flex shrink-0 items-end justify-center ${className}`}
      style={{ width: size, height: size, overflow: 'visible' }}>
      <span className="rd-cast-body" style={{ width: size, height: size }}>
        {inline
          ? <RiggedDrawing key={`${cast}-${size}`} text={text!.text} mood={mood} size={size} onPosed={setRigged} />
          : <img src={castSrc(cast, glyph)} alt="" width={size} height={size} draggable={false}
              decoding="async" onError={() => setFailed(true)}
              style={{ width: size, height: size, display: 'block' }} />}
      </span>
      {mood === 'podium' && <Crown size={size} />}
      {mood === 'right' && <Sparks size={size} />}
      {mood === 'none' && !(inline && rigged) && <span className="rd-cast-z" aria-hidden="true" style={{ fontSize: Math.max(10, size * 0.16) }}>z</span>}
    </span>
  );
}

/** A small three-point crown, ours, above the character. */
function Crown({ size }: { size: number }) {
  const w = size * 0.46;
  return (
    <svg className="rd-cast-crown" viewBox="0 0 40 26" width={w} height={w * 0.65} aria-hidden="true"
      style={{ top: -w * 0.42 }}>
      <path d="M3 23 L3 8 L12 15 L20 3 L28 15 L37 8 L37 23 Z" fill="#ffc800" stroke="#e5a000" strokeWidth="2.4" strokeLinejoin="round" />
      <rect x="3" y="19" width="34" height="4" fill="#e5a000" />
      <circle cx="20" cy="3.2" r="2.6" fill="#ff4b4b" />
      <circle cx="3" cy="8" r="2.2" fill="#7c3aed" />
      <circle cx="37" cy="8" r="2.2" fill="#7c3aed" />
    </svg>
  );
}

const SPARKS: ReadonlyArray<[number, number, number, number]> = [
  // x %, y %, scale, delay ms
  [8, 18, 1, 120], [88, 12, 0.8, 200], [96, 58, 0.6, 260], [2, 62, 0.7, 300], [50, -8, 0.9, 160],
];

/** A short burst of four-point sparks around the character (the right answer). */
function Sparks({ size }: { size: number }) {
  const s = Math.max(8, size * 0.16);
  return (
    <>
      {SPARKS.map(([x, y, k, d], i) => (
        <svg key={i} className="rd-cast-spark" viewBox="0 0 20 20" width={s * k} height={s * k} aria-hidden="true"
          style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${d}ms` }}>
          <path d="M10 0 C11 7 13 9 20 10 C13 11 11 13 10 20 C9 13 7 11 0 10 C7 9 9 7 10 0 Z" fill={i % 2 ? '#ffc800' : '#58cc02'} />
        </svg>
      ))}
    </>
  );
}
